import type React from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CHART_PALETTE } from "../../types";

export interface DonutSlice {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartWidgetProps {
  data: DonutSlice[];
  height?: number;
  centerLabel?: string;
  formatter?: (value: number) => string;
}

export function DonutChartWidget({
  data,
  height = 280,
  centerLabel,
  formatter,
}: DonutChartWidgetProps) {
  const filtered = data.filter((s) => s.value > 0);
  if (filtered.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No data available for selected filters
      </div>
    );
  }
  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={filtered}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={1}
          >
            {filtered.map((s, idx) => (
              <Cell
                key={s.name}
                fill={s.color ?? CHART_PALETTE[idx % CHART_PALETTE.length]}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              border: "1px solid hsl(210,16%,85%)",
              borderRadius: 6,
              fontSize: 12,
            }}
            formatter={(value) => {
              if (typeof value === "number" && formatter) return formatter(value);
              return value as React.ReactNode;
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            layout="vertical"
            verticalAlign="middle"
            align="right"
          />
        </PieChart>
      </ResponsiveContainer>
      {centerLabel && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center" style={{ paddingRight: 100 }}>
            <div className="text-[18px] font-semibold text-foreground">
              {centerLabel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
